/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  isReadAloudPausedByButton,
  isNextButtonDisabled,
  isPrevButtonDisabled,
  getVoiceRateList,
  getSelectedRateIndex,
  getSelectedVoiceIndex,
  getVoiceList,
  isReadAloudActive
} from "selectors/readAloudSelectors";

export const getReadAloudApis = stores => {
  async function start() {
    if (!isReadAloudActive(stores)) return stores.readAloudStore.startHandler();
    return false;
  }

  function stop() {
    if (isReadAloudActive(stores)) {
      stores.readAloudStore.stopHandler();
      return true;
    }
    return false;
  }

  function isPaused() {
    return isReadAloudActive(stores) && isReadAloudPausedByButton(stores);
  }

  function pause() {
    if (!isReadAloudPausedByButton(stores) && isReadAloudActive(stores)) {
      stores.readAloudStore.pauseButtonHandler();
      return true;
    }
    return false;
  }

  function play() {
    if (isReadAloudPausedByButton(stores) && isReadAloudActive(stores)) {
      stores.readAloudStore.playButtonHandler();
      return true;
    }
    return false;
  }

  function hasNext() {
    return isReadAloudActive(stores) ? !isNextButtonDisabled(stores) : false;
  }

  function hasPrev() {
    return isReadAloudActive(stores) ? !isPrevButtonDisabled(stores) : false;
  }

  function next() {
    if (!isNextButtonDisabled(stores) && isReadAloudActive(stores)) {
      stores.readAloudStore.next();
      return true;
    }
    return false;
  }

  function prev() {
    if (!isPrevButtonDisabled(stores) && isReadAloudActive(stores)) {
      stores.readAloudStore.prev();
      return true;
    }
    return false;
  }

  function getVoiceRates() {
    return getVoiceRateList(stores);
  }

  function setVoiceRate(index) {
    const voiceRates = getVoiceRateList(stores);
    if (index >= 0 && index < voiceRates.length) {
      stores.readAloudStore.setSelectedRateIndex(index);
      return true;
    }
    return false;
  }

  function getSelectedVoiceRate() {
    return getSelectedRateIndex(stores);
  }

  function getVoiceOptions() {
    const voiceList = getVoiceList(stores);
    return voiceList.map(({ name, lang }) => ({
      name,
      lang
    }));
  }

  function setVoiceOption(index) {
    const voiceList = getVoiceList(stores);
    if (index >= 0 && index < voiceList.length) {
      stores.readAloudStore.setSelectedVoiceIndex(index);
      return true;
    }
    return false;
  }

  function getSelectedVoiceOption() {
    return getSelectedVoiceIndex(stores);
  }

  return {
    readAloud: {
      start,
      stop,
      isPaused,
      pause,
      play,
      hasNext,
      hasPrev,
      next,
      prev,
      getVoiceRates,
      setVoiceRate,
      getSelectedVoiceRate,
      getVoiceOptions,
      setVoiceOption,
      getSelectedVoiceOption
    }
  };
};
